import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Lock, Music, UserCircle2, UserPlus } from "lucide-react";
import { artistsMgmtApi } from "./api";
import type { ArtistListItem, ArtistListStatus } from "./types";
import { RegisterArtistModal } from "./RegisterArtistModal";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useDebounce } from "@/lib/utils/useDebounce";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const STATUS_OPTIONS: { value: ArtistListStatus | ""; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "active", label: "Activos" },
  { value: "blocked", label: "Bloqueados" },
  { value: "deleted", label: "Borrados (soft)" },
];

const PAGE_SIZE = 25;

export function ArtistsScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ArtistListStatus | "">("");
  const [page, setPage] = useState(1);
  const [showRegister, setShowRegister] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const query = useQuery({
    queryKey: ["artists-mgmt", "list", { q: debouncedSearch, status, page }],
    queryFn: () =>
      artistsMgmtApi.list({
        q: debouncedSearch || undefined,
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<ArtistListItem>[] = [
    {
      key: "artist",
      header: "Artista",
      cell: (a) => {
        const avatar = a.artistImg || a.img;
        const displayName =
          a.artistName ||
          [a.firstName, a.lastName].filter(Boolean).join(" ") ||
          a.userName ||
          a.email;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            {avatar ? (
              <img
                src={avatar}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-border"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-bg-subtle border border-border flex items-center justify-center text-fg-muted">
                <UserCircle2 size={16} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg truncate">
                {displayName}
              </div>
              <div className="text-xs text-fg-subtle truncate">{a.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: "flags",
      header: "Estado",
      cell: (a) => (
        <div className="flex flex-wrap gap-1.5">
          {a.deletedAt && (
            <span className="nx-badge bg-danger-soft text-danger">Borrado</span>
          )}
          {a.isBlocked && (
            <span className="nx-badge bg-danger-soft text-danger">
              <Lock size={10} className="inline mr-0.5" /> Bloqueado
            </span>
          )}
          {!a.deletedAt && !a.isBlocked && (
            <span className="nx-badge bg-success-soft text-success">Activo</span>
          )}
          {!a.linkedArtistaId && (
            <span className="nx-badge bg-warning-soft text-warning">Sin ficha</span>
          )}
          {a.isProducer && (
            <span className="nx-badge bg-accent-soft text-accent">Productor</span>
          )}
          {a.isPartner && (
            <span className="nx-badge bg-accent-soft text-accent">Partner</span>
          )}
        </div>
      ),
    },
    {
      key: "genres",
      header: "Géneros",
      cell: (a) =>
        a.genres.length ? (
          <div className="flex flex-wrap gap-1">
            {a.genres.slice(0, 2).map((g) => (
              <span key={g} className="nx-badge bg-bg-subtle text-fg-muted">
                {g}
              </span>
            ))}
            {a.genres.length > 2 && (
              <span className="text-xs text-fg-subtle">+{a.genres.length - 2}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-fg-subtle">—</span>
        ),
    },
    {
      key: "offers",
      header: "Bookings",
      align: "right",
      width: "min",
      cell: (a) => (
        <span className="tabular-nums text-sm text-fg inline-flex items-center gap-1">
          <Music size={12} className="text-fg-subtle" />
          {a.offerCount}
        </span>
      ),
    },
    {
      key: "events",
      header: "Line-up",
      align: "right",
      width: "min",
      cell: (a) => (
        <span className="tabular-nums text-sm text-fg inline-flex items-center gap-1">
          <CalendarDays size={12} className="text-fg-subtle" />
          {a.eventCount}
        </span>
      ),
    },
    {
      key: "created",
      header: "Registrado",
      align: "right",
      width: "min",
      cell: (a) => (
        <span className="text-xs text-fg-subtle whitespace-nowrap">
          {formatDateTime(a.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="nx-h1">Artistas</h1>
          <p className="nx-eyebrow text-fg-muted mt-1">
            Gestiona el catálogo de artistas y da de alta nuevos sin que pasen
            por postulación. Quedan activos y bookeables al instante.
          </p>
        </div>
        <Can permission={PERMISSIONS.ARTISTS_MGMT_EDIT}>
          <button
            type="button"
            onClick={() => setShowRegister(true)}
            className="nx-btn-primary shrink-0"
          >
            <UserPlus size={14} />
            Registrar artista
          </button>
        </Can>
      </div>

      <FiltersBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Buscar por email, nombre o usuario…"
        status={status}
        onStatusChange={(v) => {
          setStatus(v as ArtistListStatus | "");
          setPage(1);
        }}
        statusOptions={STATUS_OPTIONS}
      />

      {query.isLoading && <LoadingBlock label="Cargando artistas…" />}
      {query.isError && <ErrorBlock onRetry={() => query.refetch()} />}
      {query.data && (
        <ApplicationsTable
          rows={query.data.artists}
          columns={columns}
          page={query.data.page}
          pageSize={query.data.pageSize}
          total={query.data.total}
          onPageChange={setPage}
          onRowClick={(a) => navigate(`/artists/${a.id}`)}
          emptyLabel="No hay artistas que coincidan con los filtros."
        />
      )}

      {showRegister && (
        <RegisterArtistModal onClose={() => setShowRegister(false)} />
      )}
    </div>
  );
}
